// formSubmit.js
// Shared helpers for the two "send an order/request to send-mail.php" flows
// (boutique cart + catering calculator). Both forms follow the exact same
// shape: validate -> disable button -> POST -> on success clear form + swap
// submit button for a "send another" button -> on failure re-enable button
// and show an error. This module owns that shared shape so the two
// page-modules only need to supply their own validation + payload.

const LOG = (...args) => console.log('[FormSubmit]', ...args);
const WARN = (...args) => console.warn('[FormSubmit]', ...args);

/**
 * Show (or create) a status message box right before the submit button.
 * Scrolls it into view and focuses it for screen readers.
 *
 * @param {object} opts
 * @param {HTMLElement} opts.submitBtn - submit button the status box is anchored above
 * @param {string} opts.statusId - id to use/reuse for the status div
 * @param {string} opts.message
 * @param {boolean} [opts.isError=false]
 * @returns {HTMLElement} the status div (so callers can cache/reuse it)
 */
export function showFormStatus({ submitBtn, statusId, message, isError = false }) {
  let statusDiv = document.getElementById(statusId);
  if (!statusDiv) {
    statusDiv = document.createElement('div');
    statusDiv.id = statusId;
    statusDiv.className = 'form-status';
    submitBtn.parentNode.insertBefore(statusDiv, submitBtn);
  }
  statusDiv.className = `form-status ${isError ? 'error' : 'success'}`;
  statusDiv.textContent = message;
  statusDiv.style.display = 'block';
  statusDiv.setAttribute('tabindex', '-1');

  setTimeout(() => {
    statusDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
    statusDiv.focus({ preventScroll: true });
  }, 100);

  LOG('showFormStatus', isError ? 'ERROR' : 'OK', message);
  return statusDiv;
}

/**
 * Create (or show, if already created) a "send another" reset button right
 * after the submit button, and hide the submit button + status box.
 *
 * @param {object} opts
 * @param {HTMLElement} opts.submitBtn
 * @param {string} opts.resetBtnId
 * @param {string} opts.resetLabel
 * @param {() => void} [opts.onReset] - extra cleanup to run when the reset button is clicked
 */
export function showResetButton({ submitBtn, resetBtnId, resetLabel, onReset }) {
  submitBtn.style.display = 'none';

  let resetBtn = document.getElementById(resetBtnId);
  if (!resetBtn) {
    resetBtn = document.createElement('button');
    resetBtn.id = resetBtnId;
    resetBtn.type = 'button';
    resetBtn.className = 'submit-btn';
    resetBtn.textContent = resetLabel;
    resetBtn.style.cssText = 'margin-top: 1rem; background-color: #4CAF50;';
    submitBtn.parentNode.insertBefore(resetBtn, submitBtn.nextSibling);

    resetBtn.addEventListener('click', () => {
      submitBtn.style.display = '';
      submitBtn.disabled = false;
      resetBtn.style.display = 'none';
      const statusDiv = submitBtn.parentNode.querySelector('.form-status');
      if (statusDiv) statusDiv.style.display = 'none';
      onReset?.();
      LOG('reset button clicked', resetBtnId);
    });
} else {
    // Remove and recreate so the click handler closes over the current submitBtn
    resetBtn.remove();
    resetBtn = null;
    return showResetButton({ submitBtn, resetBtnId, resetLabel, onReset });
}
  return resetBtn;
}

/**
 * Run the full submit -> POST send-mail.php -> success/failure flow shared
 * by both forms.
 *
 * @param {object} opts
 * @param {HTMLElement} opts.submitBtn
 * @param {string} opts.statusId
 * @param {string} opts.sendingLabel - button text while in-flight
 * @param {string} opts.idleLabel - button text to restore on failure
 * @param {object} opts.payload - JSON body to POST to send-mail.php
 * @param {(result: object) => string} opts.successMessage - builds the success status text from the server response
 * @param {object} opts.resetButton - { id, label, onReset? } passed to showResetButton on success
 * @param {() => void} [opts.onSuccess] - extra cleanup (clear cart/form fields, reset captcha, etc.)
 */
export async function submitOrderForm({
  submitBtn,
  statusId,
  sendingLabel,
  idleLabel,
  payload,
  successMessage,
  resetButton,
  onSuccess
}) {
  submitBtn.disabled = true;
  submitBtn.textContent = sendingLabel;

  try {
    const response = await fetch('/server/send-mail.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    LOG('submitOrderForm: POST', response.url, response.status);

    const text = await response.text();
    LOG('submitOrderForm: server response', text);

    let result = null;
    try {
      result = text ? JSON.parse(text) : null;
    } catch (err) {
      WARN('submitOrderForm: response not JSON', err);
      showFormStatus({
        submitBtn,
        statusId,
        message: '❌ Erreur serveur : ' + (text.substring(0, 300) || response.statusText),
        isError: true
      });
      submitBtn.disabled = false;
      submitBtn.textContent = idleLabel;
      return;
    }

    if (response.ok && result?.success) {
      LOG('submitOrderForm: success');
      showFormStatus({
        submitBtn,
        statusId,
        message: successMessage(result),
        isError: false
      });

      onSuccess?.(result);

      showResetButton({
        submitBtn,
        resetBtnId: resetButton.id,
        resetLabel: resetButton.label,
        onReset: resetButton.onReset
      });
    } else {
      const errorMsg = result?.error || "Échec de l'envoi. Veuillez réessayer.";
      showFormStatus({ submitBtn, statusId, message: '❌ ' + errorMsg, isError: true });
      submitBtn.disabled = false;
      submitBtn.textContent = idleLabel;
      WARN('submitOrderForm: server error', errorMsg);
    }
  } catch (error) {
    console.error('[FormSubmit] submitOrderForm network error:', error);
    showFormStatus({
      submitBtn,
      statusId,
      message: '❌ Erreur réseau. Vérifiez votre connexion.',
      isError: true
    });
    submitBtn.disabled = false;
    submitBtn.textContent = idleLabel;
  }
}

/*********************************************
 * Shared field validation
 *********************************************/

export const SHARED_LIMITS = {
  NAME_MAX_LENGTH: 100,
  EMAIL_MAX_LENGTH: 150,
  PHONE_MAX_LENGTH: 30,
  MESSAGE_MAX_LENGTH: 1000
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-() ]{7,}$/;

/**
 * Validate the shared name/email/phone/message fields every order form
 * collects. Returns an i18n-keyed error string, or null if all good.
 * Page-specific fields (pickup mode, captcha, cart contents, etc.) are
 * validated separately by the caller.
 *
 * @param {{name: string, email: string, phone: string, message: string}} fields
 * @param {(key: string) => string | undefined} getText - langManager.getText, for translated fallbacks
 */
export function validateSharedFields({ name, email, phone, message }, getText) {
  if (!name || !email || !phone) {
    return getText('fillAllFields') || 'Veuillez remplir tous les champs requis.';
  }
  if (name.length > SHARED_LIMITS.NAME_MAX_LENGTH) {
    return `Le nom doit faire moins de ${SHARED_LIMITS.NAME_MAX_LENGTH} caractères.`;
  }
  if (email.length > SHARED_LIMITS.EMAIL_MAX_LENGTH || !EMAIL_RE.test(email)) {
    return getText('validEmail') || 'Veuillez entrer un email valide.';
  }
  if (phone.length > SHARED_LIMITS.PHONE_MAX_LENGTH || !PHONE_RE.test(phone)) {
    return 'Veuillez entrer un numéro de téléphone valide.';
  }
  if (message.length > SHARED_LIMITS.MESSAGE_MAX_LENGTH) {
    return `Le message doit faire moins de ${SHARED_LIMITS.MESSAGE_MAX_LENGTH} caractères.`;
  }
  return null;
}
